import { LOCATION_CLASSIFICATION } from "@/components/alerts/constants";

import { BulletinLocation } from "./types";

/**
 * The per-row location rules, as a pure module.
 *
 * The fourth of the rule modules `model/incidentTimes.ts` describes, and it exists for the same
 * reason: the schema and the row have to agree about what is wrong with a location, and a function
 * is far easier to pin down than a rendered row. `validation/schemas.ts` calls this in a
 * `superRefine`; `fields/LocationRow` calls it to decide what to mark red. Neither restates it.
 *
 * Errors are **codes**, not messages — `model/entityRules.ts` explains why. Each code is unique
 * within its module, so every reader renders it the same way: `t(`validation.location_${code}`)`.
 *
 * The one rule that is deliberately *not* here: an address whose geocode has not landed. The old
 * flow blocked saving on `validateGeocodedAddresses`, which is what made saving depend on
 * `window.google.maps` being loaded. Decision 4 geocodes once, at selection, and stores the result;
 * whether that request came back is not something the officer can act on, so it does not gate
 * Finalize.
 *
 * What *is* here, and reads like the same thing but is not: an address carrying text and no
 * `place_id`. That means no Google prediction was ever picked. Imports are where it comes from —
 * PDF extraction writes the model's own wording into `location.addresses` with no `place_id` and no
 * `geometry`, and the report hand-off does the same on its `raw_address` branch — so the row arrives
 * looking filled in while holding a string nobody has matched to a real place. Unlike a geocode that
 * has not come back, this *is* something the officer can act on: pick the address from the list. So
 * it gates Finalize.
 */

export type LocationErrorCode =
  | "address_required"
  | "address_not_selected"
  | "type_required"
  | "specify_type";

export type LocationErrors = Partial<
  Record<"input" | "classification" | "otherClassification", LocationErrorCode>
>;

/**
 * A row nobody has touched. Row errors are shown once a row has content — a blank row's only real
 * problem is that it is blank, and the list-level "add an incident location" already says so.
 */
export const locationRowHasContent = (location: BulletinLocation): boolean =>
  location.input !== null || location.classification !== null;

/**
 * Every row that exists is required, so a row missing its address is an error rather than an empty
 * slot — the officer removes a row instead of leaving it blank.
 *
 * A row with no address yields only that one error: the type it is also missing is not a second
 * thing to fix, and reporting both would count one empty row twice in "N required fields left".
 */
export const validateLocationRow = (
  location: BulletinLocation,
): LocationErrors => {
  if (!location.input) return { input: "address_required" };

  // Returns alone, for the same reason `address_required` does: an address that is not a real place
  // is the row's problem, and pairing it with a missing type would count one broken row twice.
  if (!location.input.place_id) return { input: "address_not_selected" };

  if (location.classification === null) {
    return { classification: "type_required" };
  }

  return location.classification === LOCATION_CLASSIFICATION.OTHER &&
    !location.otherClassification.trim()
    ? { otherClassification: "specify_type" }
    : {};
};
